import { useRef, type ReactNode } from 'react';
import { Camera, ImagePlus, Loader2, Trash2 } from 'lucide-react';

import { ACCEPTED_IMAGE_TYPES, checkImageFile } from '@/components/ui/ImageUpload';
import { Menu, type MenuItem } from '@/components/ui/Menu';
import { cn } from '@/lib/utils';

interface InstituteLogoMarkProps {
  /** The logo, or the placeholder icon when there is none. */
  children: ReactNode;
  /**
   * Whether the viewer may change the logo. Support staff see the mark only:
   * the upload and remove endpoints are SUPER_ADMIN writes.
   */
  canEdit: boolean;
  hasLogo: boolean;
  displayName: string;
  isUploading: boolean;
  onSelect: (file: File) => void;
  onRemove: () => void;
  /** A file the browser already knows the server will refuse. */
  onInvalid: (message: string) => void;
}

/**
 * The record header's mark, doubling as the place to change it.
 *
 * The logo is changed where it is seen rather than in a separate panel lower
 * down: the header is where anybody checking "is that the right logo?" is
 * already looking, and a second copy of the same image further down the page
 * only made the reader wonder which one was live.
 */
export function InstituteLogoMark({
  children,
  canEdit,
  hasLogo,
  displayName,
  isUploading,
  onSelect,
  onRemove,
  onInvalid,
}: InstituteLogoMarkProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFile(file: File | undefined) {
    if (!file) return;
    const problem = checkImageFile(file);
    if (problem) onInvalid(problem);
    else onSelect(file);
  }

  if (!canEdit) return <>{children}</>;

  const items: MenuItem[] = [
    {
      key: 'change',
      label: hasLogo ? 'Change logo' : 'Upload logo',
      description: 'Square PNG, JPG, WEBP or SVG, up to 5 MB.',
      icon: <ImagePlus className="h-4 w-4" />,
      onSelect: () => inputRef.current?.click(),
    },
  ];
  if (hasLogo) {
    items.push({
      key: 'remove',
      label: 'Remove logo',
      icon: <Trash2 className="h-4 w-4" />,
      tone: 'danger',
      onSelect: onRemove,
    });
  }

  return (
    <>
      <Menu
        label={`Change ${displayName}'s logo`}
        align="start"
        disabled={isUploading}
        items={items}
        triggerClassName="group relative h-full w-full rounded-none disabled:opacity-100"
        trigger={
          <>
            {children}
            {/* Shown on hover and keyboard focus, and held while uploading so the
                old logo is never mistaken for the new one. */}
            <span
              className={cn(
                'absolute inset-0 grid place-items-center bg-media-scrim text-media-ink transition-opacity',
                !isUploading && 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
              )}
              aria-hidden
            >
              {isUploading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Camera className="h-5 w-5" />
              )}
            </span>
          </>
        }
      />

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(event) => {
          handleFile(event.target.files?.[0]);
          // Cleared so picking the same file again after a failure still fires.
          event.target.value = '';
        }}
      />
    </>
  );
}
